import { createContext, type FC, type PropsWithChildren, useContext, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { getConfig, getLoginContext } from "./server";
import StorSpinner from "../components/stor-spinner";
import { getMobilBankState } from "../hooks/use-navigate/useNavigateMBC";
import { configureStore } from "../store/configureStore";
import ErrorPage from "~/spa/error/ErrorPage";

export type LoginContext = "NETTBANK" | "MOBILBANK" | "FORSIKRING";

export interface IpsInfo {
	gammelSparegrense: number;
	sparegrenseArlig: number;
	sparegrenseManedlig: number;
	skattefordel: number;
	skattefordelProsent: number;
	startAlder: number;
}

export interface Config {
	avtalegiroUrl: string;
	loginUrl: string;
	loginBaseUrl: string;
	kundeforsikringBaseUrl: string;
	chatCustomer: string;
	chatUrl: string;
	radgiverportalenBmUrl: string;
	showF2100KontoutskriftMessage: boolean;
	showNjordKontoutskriftMessage: boolean;
	showArsoppgaveMessage: boolean;
	nettbankHostname: string;
	alminneligSkatt: number;
	gVerdi: number;
	gVerdiDato: number;
	isProduction: boolean;
	aksjeSkatt: number;
	verdsettelsesrabatt: number;
	skatteaar: number;
	isMock: boolean;
	isDemo: boolean;
	enabledNjordProducts: string[];
	readingMode: boolean;
	isRadgiver: boolean;
	nettbankSemanticUrl: string;
	ips: IpsInfo;
	loIpsPskEnabled: boolean;
	stoppDokumenthenting: boolean;
	tilgangTilRadgiverassistertEpk: boolean;
	arligPaminnelseStartDato: Date;
	sb1fKontonr: string;
	loginContext: LoginContext;
	flexibleMaxAmount: number;
}

export const ConfigContext = createContext<Config>({} as Config);

const useConfig = () => {
	return useContext(ConfigContext);
};

export const ConfigProvider: FC<PropsWithChildren> = ({ children }) => {
	const mobilState = getMobilBankState();
	const [config, setConfig] = useState<Config>(mobilState.config);
	const [err, setErr] = useState<Error>();

	const errorHandler = (e: Error) => {
		setErr(e);
	};

	useMemo(() => {
		if (!config) {
			Promise.all([getLoginContext(), getConfig()])
				.then(([loginContext, configData]) => {
					setConfig({ ...configData, loginContext });
				})
				.catch(errorHandler);
		}
	}, []);

	if (err) {
		const store = configureStore({});
		return (
			<Provider store={store}>
				<ErrorPage error={err} resetErrorBoundary={() => setErr(undefined)} />
			</Provider>
		);
	}

	if (!config) {
		return <StorSpinner />;
	}

	return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
};
export default useConfig;
